import { type JSONContent, mergeAttributes, Node } from '@tiptap/core';
import { type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';

import { uuidRegExpString } from '~/utils/Url';

const workspaceAgentSkillIdRegExp = new RegExp(`^${uuidRegExpString}$`, 'i');

export const WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME = 'workspaceAgentSkillMoniker';

const WORKSPACE_AGENT_SKILL_MONIKER_MARKER = 'data-workspace-agent-skill-moniker';

export type WorkspaceAgentSkillMonikerAttrs = {
  name: string;
  skillId: string;
  slashCommand: string;
};

/** Pasted HTML: accept only a catalog skill id; never trust slashCommand/name from the clipboard. */
export const parseWorkspaceAgentSkillMonikerHtmlAttrs = (
  element: HTMLElement,
): WorkspaceAgentSkillMonikerAttrs | false => {
  const rawSkillId = element.dataset.workspaceAgentSkillId?.trim() ?? '';

  if (!workspaceAgentSkillIdRegExp.test(rawSkillId)) {
    return false;
  }

  return { name: '', skillId: rawSkillId, slashCommand: '' };
};

export const readWorkspaceAgentSkillMonikerAttrs = (
  attrs: Record<string, unknown>,
): WorkspaceAgentSkillMonikerAttrs | null => {
  const skillId = typeof attrs.skillId === 'string' ? attrs.skillId.trim() : '';
  const slashCommand = typeof attrs.slashCommand === 'string' ? attrs.slashCommand.trim() : '';
  const name = typeof attrs.name === 'string' ? attrs.name : '';

  if (!workspaceAgentSkillIdRegExp.test(skillId)) {
    return null;
  }

  return { name, skillId, slashCommand };
};

/** ProseMirror nodes for a skill moniker atom + trailing space. */
export const createWorkspaceAgentSkillMonikerInsertNodes = (
  schema: Schema,
  attrs: WorkspaceAgentSkillMonikerAttrs,
): PMNode[] => {
  const nodeType = schema.nodes[WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME];
  const skillNode = nodeType?.create(attrs);

  if (!skillNode) {
    return [];
  }

  return [skillNode, schema.text(' ')];
};

export const workspaceAgentSkillMonikerInsertContent = (attrs: WorkspaceAgentSkillMonikerAttrs): JSONContent[] => [
  {
    attrs,
    type: WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME,
  },
  { text: ' ', type: 'text' },
];

const BACKSPACE_KEY = new PluginKey('workspaceAgentSkillMonikerBackspace');

const findPrecedingSkillMoniker = (view: EditorView, pos: number): { from: number } | null => {
  let scanPos = pos;

  while (scanPos > 0 && /\s/.test(view.state.doc.textBetween(scanPos - 1, scanPos))) {
    scanPos -= 1;
  }

  const nodeBefore = view.state.doc.resolve(scanPos).nodeBefore;

  if (nodeBefore?.type.name !== WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME) {
    return null;
  }

  return { from: scanPos - nodeBefore.nodeSize };
};

const deleteSkillMonikerBeforeCursor = (view: EditorView): boolean => {
  const { selection } = view.state;

  if (!selection.empty) {
    return false;
  }

  const match = findPrecedingSkillMoniker(view, selection.from);

  if (!match) {
    return false;
  }

  view.dispatch(view.state.tr.delete(match.from, selection.from));
  view.focus();

  return true;
};

/**
 * Inline atom binding `skillId` to visible `/slashCommand` text. Serialized as `@[skill:<id>]`, not a pill.
 */
export const WorkspaceAgentSkillMonikerNode = Node.create({
  addAttributes() {
    return {
      name: { default: '' },
      skillId: {
        default: null,
        renderHTML: (attributes) => (attributes.skillId ? { 'data-workspace-agent-skill-id': attributes.skillId } : {}),
      },
      slashCommand: {
        default: null,
        renderHTML: (attributes) =>
          attributes.slashCommand ? { 'data-workspace-agent-skill-command': attributes.slashCommand } : {},
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: BACKSPACE_KEY,
        props: {
          handleKeyDown(view, event) {
            if (event.key !== 'Backspace') {
              return false;
            }

            if (!deleteSkillMonikerBeforeCursor(view)) {
              return false;
            }

            event.preventDefault();

            return true;
          },
        },
      }),
    ];
  },

  atom: true,
  group: 'inline',
  inline: true,
  name: WORKSPACE_AGENT_SKILL_MONIKER_NODE_NAME,

  parseHTML() {
    return [
      {
        getAttrs: (element: HTMLElement) => parseWorkspaceAgentSkillMonikerHtmlAttrs(element),
        tag: `span[${WORKSPACE_AGENT_SKILL_MONIKER_MARKER}]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    const slashCommand =
      typeof node.attrs.slashCommand === 'string' && node.attrs.slashCommand ? node.attrs.slashCommand : '';

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-testid': 'WORKSPACE_AGENT_COMPOSER_SKILL_MONIKER',
        'data-workspace-agent-skill-moniker': '',
      }),
      slashCommand ? `/${slashCommand}` : '',
    ];
  },
});
